import { format } from 'date-fns';

import type { ReadinessFormula } from '@/lib/api/readiness';
import type {
  ReadinessTrend,
  ReadinessTrendPoint,
  ReviewCounts,
  ReviewMovement,
  StudentMovementRow,
  TrendDirection,
} from '@/lib/api/review';

/** Tone for a movement, shared by the trend pill and the row badges. */
export const MOVEMENT_TONE: Record<ReviewMovement, string> = {
  slipped: 'text-rose-700 bg-rose-50 border-rose-200',
  improved: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  flat: 'text-slate-600 bg-slate-50 border-slate-200',
  new: 'text-sky-700 bg-sky-50 border-sky-200',
  no_data: 'text-amber-700 bg-amber-50 border-amber-200',
};

export const DIRECTION_LABEL: Record<TrendDirection, string> = {
  improving: 'Improving',
  flat: 'Holding steady',
  slipping: 'Slipping',
  unknown: 'Not enough history',
};

export const DIRECTION_TONE: Record<TrendDirection, string> = {
  improving: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  flat: 'text-slate-600 bg-slate-50 border-slate-200',
  slipping: 'text-rose-700 bg-rose-50 border-rose-200',
  unknown: 'text-muted-foreground bg-muted border-transparent',
};

/**
 * Re-expresses a UTC instant as the equivalent LOCAL date, so a formatter that
 * works in local time renders the UTC calendar day.
 *
 * Review periods are UTC-anchored Mondays. Formatting one directly in local
 * time would show the previous Sunday to anyone west of UTC, relabelling every
 * point on the chart and disagreeing with the period the server grouped by.
 * Returns null for an unparseable input so callers render nothing rather than
 * "Invalid Date".
 */
function utcCalendarDate(iso: string): Date | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * A short ISO week label: "31 Aug".
 *
 * An explicit pattern, not toLocaleDateString: that follows the runtime's
 * locale, so the same build renders "31 Aug" on one machine and "Aug 31" on
 * another. `dd MMM yyyy` is already the convention elsewhere in this app,
 * including the batch header this panel sits under.
 */
export function formatPeriod(iso: string): string {
  const d = utcCalendarDate(iso);
  return d ? format(d, 'dd MMM') : '';
}

/** "31 Aug 2026" — for the range caption, where the year matters. */
export function formatPeriodLong(iso: string): string {
  const d = utcCalendarDate(iso);
  return d ? format(d, 'dd MMM yyyy') : '';
}

/**
 * The point of the review, in one sentence, ordered by what to act on.
 *
 * Deliberately not a list of every count — a TPO reading "26 students, 26
 * unchanged" learns everything they need without parsing nine numbers, and a
 * sentence that leads with the at-risk figure cannot be skimmed past.
 */
export function summariseReview(counts: ReviewCounts, collecting: boolean): string {
  if (collecting) return 'Collecting the first measurements';
  if (counts.total === 0) return 'No students in this period';

  const parts: string[] = [];
  if (counts.newly_at_risk > 0) parts.push(`${counts.newly_at_risk} newly at risk`);
  if (counts.slipped > 0) parts.push(`${counts.slipped} slipped`);
  if (counts.improved > 0) parts.push(`${counts.improved} improved`);
  if (counts.stalled > 0) parts.push(`${counts.stalled} inactive`);
  if (parts.length === 0) {
    return `No movement across ${counts.total} ${counts.total === 1 ? 'student' : 'students'}`;
  }
  return parts.join(' · ');
}

/**
 * Chart series for a trend. Points with no data become `null` so the line
 * breaks rather than dropping to the axis: a week before a student's first
 * assessment is unmeasured, and drawing it at zero would show every new
 * student as having recovered from catastrophe.
 */
export interface TrendSeriesPoint {
  period: string;
  label: string;
  score: number | null;
  tier?: string;
  backfilled: boolean;
  coverage: number;
}

export function trendSeries(points: ReadinessTrendPoint[]): TrendSeriesPoint[] {
  return points.map((p) => ({
    period: p.period_start,
    label: formatPeriod(p.period_start),
    score: p.has_data ? p.score : null,
    tier: p.tier,
    backfilled: p.backfilled,
    coverage: p.data_coverage,
  }));
}

/** How many points in the series were actually measured. */
export function measuredCount(trend: ReadinessTrend | undefined): number {
  return (trend?.points ?? []).filter((p) => p.has_data).length;
}

/** True when every measured point was reconstructed rather than captured live. */
export function isFullyBackfilled(trend: ReadinessTrend | undefined): boolean {
  const measured = (trend?.points ?? []).filter((p) => p.has_data);
  return measured.length > 0 && measured.every((p) => p.backfilled);
}

/**
 * Why a row appears where it does, in the reader's words. Returned instead of
 * a bare movement label so the list explains its own ordering — the same
 * standard the score itself is now held to.
 */
export function explainMovement(
  row: StudentMovementRow,
  thresholds: { score_delta: number; stale_days: number },
): string {
  if (row.movement === 'no_data') {
    return 'Too little measured data to compare periods';
  }
  if (row.movement === 'new') {
    return 'First period on record — nothing to compare against yet';
  }

  const bits: string[] = [];
  if (row.newly_at_risk) {
    bits.push('Dropped into the bottom tier this period');
  } else if (row.tier_changed && row.previous_tier) {
    bits.push(`Moved from tier ${row.previous_tier} to ${row.tier}`);
  } else if (row.delta !== undefined && Math.abs(row.delta) >= thresholds.score_delta) {
    bits.push(`${row.delta > 0 ? 'Gained' : 'Lost'} ${Math.abs(row.delta)} points`);
  } else {
    bits.push('Within the noise threshold');
  }

  if (row.stalled) {
    bits.push(
      row.days_since_activity === undefined
        ? 'no activity on record'
        : `no activity for ${row.days_since_activity} days`,
    );
  }
  return bits.join(' · ');
}

/**
 * The score at which a student leaves the bottom band, read from the formula
 * the server publishes.
 *
 * Never hardcoded. The v1 readiness copy hardcoded weights in TSX and drifted
 * silently from the Go scorer; a hardcoded band boundary would drift the same
 * way and put the chart's "at risk" line somewhere the tier badge disagrees
 * with. Falls back to undefined so the caller can omit the line rather than
 * draw it in the wrong place.
 */
export function atRiskThresholdFrom(formula: ReadinessFormula | undefined): number | undefined {
  const tiers = formula?.tiers ?? [];
  const above = tiers.filter((t) => t.key !== 'needs_support');
  if (above.length === 0) return undefined;
  return above.reduce((min, t) => Math.min(min, t.min_score), Number.POSITIVE_INFINITY);
}
